/**
 * @file AssetDetailsPanel.js
 * @description This panel displays the editable fields for the details
 * associated with an Unassigned or Content Only asset.
 */
import { useState, useEffect } from 'react';
import { SearchableDropdown } from '../../helpers/SearchableDropdown';
import {
    InputField,
    TextAreaField,
    SwitchField,
    CheckboxField,
} from '../../helpers/FormComponents';
import TagInput from '../../helpers/TagInput';
import Tooltip from '../../ui/tooltips/Tooltip';
import { formatDateToMMDDYYYY } from '../../helpers/DateUtils';
import Toast from '../../ui/toasts/Toast';
import { searchIndividuals } from '../../../services/individualService';
import { fetchPopulationGroups } from '../../../services/populationGroupService';
import styles from '../AssetDrawer.module.scss';
import { getText } from '../../../i18n';
import { ResourceScope } from '../../../utils/data/enums';
import ResourcePropertiesModal from './ResourcePropertiesModal';
import { getFileCategory } from '../../helpers/FileUtils';

const AssetDetailsPanel = ({
    asset,
    isMyAssets,
    onNavigateToHistory,
    onNavigateToPortals,
    onNavigateToNotifications,
    onNavigateToTemplates,
    onChangeAsset,
    isGlobalAssets,
    tagInputRef,
    isAssetLibraryReadOnly = false,
    allowAssetSharing = false,
    isResourceLibrary = false,
}) => {
    const [isResourcePropertiesModalOpen, setIsResourcePropertiesModalOpen] =
        useState(false);
    const [resourcePropertiesInitialTab, setResourcePropertiesInitialTab] =
        useState('overview');
    const [resourcePropertiesModalKey, setResourcePropertiesModalKey] =
        useState(0);
    const [individualsDropdownArray, setIndividualsDropdownArray] = useState(
        [],
    );
    const [popGroupsDropdownArray, setPopGroupsDropdownArray] = useState([]);

    useEffect(() => {
        if (isResourceLibrary) {
            return;
        }
        loadIndividuals();
        loadPopGroups();
    }, [isResourceLibrary]);

    const openResourcePropertiesModal = initialTab => {
        setResourcePropertiesInitialTab(initialTab);
        setIsResourcePropertiesModalOpen(true);
    };

    const loadIndividuals = async () => {
        try {
            const fetchedIndividuals = await searchIndividuals(null);
            const formattedIndividuals = fetchedIndividuals.individuals.map(
                individual => ({
                    id: individual.id,
                    name: `${individual.firstName} ${individual.lastName}`,
                }),
            );
            setIndividualsDropdownArray(formattedIndividuals);
        } catch (error) {
            console.error('Failed to fetch individuals', error);
            Toast.error(getText('msg_aa706a18bdf8'));
        }
    };

    const loadPopGroups = async () => {
        try {
            const fetchedPopGroups = await fetchPopulationGroups(null);
            const formattedPopGroups = fetchedPopGroups.map(popGroup => ({
                id: popGroup.id,
                name: popGroup.name,
            }));
            setPopGroupsDropdownArray(formattedPopGroups);
        } catch (error) {
            console.error('Failed to fetch population groups', error);
            Toast.error(getText('msg_e5df9e5e8f12'));
        }
    };

    const renderReadOnlyValue = value => (
        <p className={styles.readOnlyValue}>{value || '-'}</p>
    );

    const getDisplayNames = values =>
        (values || [])
            .map(value => value?.name)
            .filter(Boolean)
            .join(', ');

    const resourceScopeOptions = Object.entries(ResourceScope).map(
        ([scopeName, scope]) => ({
            id: scope,
            name: scopeName === 'ClientAdmin' ? 'Client Admin' : scopeName,
            className: styles[`resourceScope${scopeName}`],
        }),
    );
    const selectedResourceScopes = resourceScopeOptions.filter(
        scope => ((asset.resourceScopeMask || 0) & scope.id) === scope.id,
    );
    const updateResourceScopes = scopes =>
        onChangeAsset(
            'resourceScopeMask',
            scopes.reduce((mask, scope) => mask | scope.id, 0),
        );
    const resourceFileCategory = getFileCategory(asset.fileType);
    const isResourceTranscriptRelevant =
        resourceFileCategory === 'video' || resourceFileCategory === 'audio';

    if (isResourceLibrary) {
        return (
            <>
                <div className="d-flex align-items-center gap-2 mb-4">
                    <div className="form-switch form-switch-locked m-0">
                        <input
                            className="form-check-input custom-switch-input"
                            type="checkbox"
                            checked={!asset.isHidden}
                            disabled={isAssetLibraryReadOnly}
                            onChange={event =>
                                onChangeAsset('isHidden', !event.target.checked)
                            }
                        />
                    </div>
                    <div className="file-form-label m-0">
                        {getText('msg_1fe59390acc1')}
                    </div>
                </div>
                {isAssetLibraryReadOnly ? (
                    <div className={styles.sectionMarginBottom24}>
                        <div className="file-form-label">
                            {getText('msg_709a23220f2c')}
                            <span className={styles.requiredAsterisk}>*</span>
                        </div>
                        {renderReadOnlyValue(asset.name)}
                    </div>
                ) : (
                    <InputField
                        label={getText('msg_709a23220f2c')}
                        onChange={value => onChangeAsset('name', value)}
                        maxChars={256}
                        required={true}
                        placeholder={getText('msg_b7ffb9789f09')}
                        value={asset.name}
                    />
                )}
                <div className={styles.resourceScopeField}>
                    <div className="file-form-label">
                        {getText('msg_0f664ce3c986')}
                        <span className={styles.requiredAsterisk}>*</span>
                    </div>
                    {!isAssetLibraryReadOnly && (
                        <SearchableDropdown
                            placeholderLabel={getText('msg_3e12847dbf80')}
                            options={resourceScopeOptions}
                            optionIDField="id"
                            savedOptions={selectedResourceScopes}
                            savedOptionIDField="id"
                            updateOptions={updateResourceScopes}
                            showTags={false}
                        />
                    )}
                    <div className={styles.resourceScopeTagBox}>
                        {selectedResourceScopes.map(scope => (
                            <button
                                key={scope.id}
                                type="button"
                                className={`${styles.resourceScopeBadge} ${scope.className}`}
                                disabled={isAssetLibraryReadOnly}
                                onClick={() =>
                                    updateResourceScopes(
                                        selectedResourceScopes.filter(
                                            selectedScope =>
                                                selectedScope.id !== scope.id,
                                        ),
                                    )
                                }
                            >
                                {!isAssetLibraryReadOnly && (
                                    <i className="bi bi-x me-1" />
                                )}
                                {scope.name}
                            </button>
                        ))}
                    </div>
                </div>
                {isAssetLibraryReadOnly ? (
                    <div className={styles.sectionMarginBottom24}>
                        <div className="file-form-label">
                            {getText('msg_1910e1df6652')}
                        </div>
                        {renderReadOnlyValue(asset.description)}
                    </div>
                ) : (
                    <TextAreaField
                        label={getText('msg_1910e1df6652')}
                        onChange={value => onChangeAsset('description', value)}
                        maxChars={200}
                        tooltip={getText('msg_73f1aadae1ad')}
                        placeholder={getText('msg_3ae3bf00ff41')}
                        value={asset.description}
                    />
                )}
                <div className={styles.tagsSection}>
                    <div className="file-form-label">
                        {getText('msg_848eed0fbd54')}
                    </div>
                    {isAssetLibraryReadOnly ? (
                        <div className={styles.readOnlyTagsContainer}>
                            {(asset.assetTags || []).map(tag => (
                                <span key={tag} className={styles.tagPill}>
                                    {tag}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <TagInput
                            ref={tagInputRef}
                            onTagsChange={tags =>
                                onChangeAsset('assetTags', tags)
                            }
                            savedTags={asset.assetTags}
                        />
                    )}
                </div>
                {!isAssetLibraryReadOnly && (
                    <div className={styles.resourceAdditionalProperties}>
                        <p>{getText('msg_25f6b617c937')}</p>
                        <button
                            type="button"
                            onClick={() =>
                                openResourcePropertiesModal('overview')
                            }
                        >
                            {getText('msg_3c74b58d9ae4')}
                        </button>

                        {isResourceTranscriptRelevant && (
                            <button
                                type="button"
                                onClick={() =>
                                    openResourcePropertiesModal('transcript')
                                }
                            >
                                {getText('msg_9adaa8c6d282')}
                            </button>
                        )}
                    </div>
                )}
                <div className={styles.historyHeaderRow}>
                    <p className={styles.heading}>
                        {getText('msg_90ccd6497400')}
                    </p>
                    <p
                        className={`${styles.heading} ${styles.viewAllLink}`}
                        onClick={onNavigateToHistory}
                    >
                        {getText('msg_efd83559209c')}
                    </p>
                </div>
                <div className={styles.historyRowLeft}>
                    <div className={styles.historyItem}>
                        <p>{getText('msg_accf40c89baa')}</p>
                        <p>{formatDateToMMDDYYYY(asset.createdAt)}</p>
                    </div>
                    <div className={styles.historyItem}>
                        <p>{getText('msg_0c79adcf41ec')}</p>
                        <p>{formatDateToMMDDYYYY(asset.updatedAt)}</p>
                    </div>
                </div>
                {isResourcePropertiesModalOpen && (
                    <ResourcePropertiesModal
                        key={`${asset.id}-${resourcePropertiesInitialTab}-${asset.updatedAt || ''}`}
                        asset={asset}
                        initialTab={resourcePropertiesInitialTab}
                        onClose={() => setIsResourcePropertiesModalOpen(false)}
                        onSave={({ overview, transcript }) => {
                            onChangeAsset('overview', overview);
                            onChangeAsset('transcript', transcript);
                        }}
                    />
                )}
            </>
        );
    }

    return (
        <>
            {isAssetLibraryReadOnly ? (
                <div className={styles.sectionMarginBottom24}>
                    <div className="file-form-label">
                        {getText('msg_709a23220f2c')}
                        <span className={styles.requiredAsterisk}>*</span>
                    </div>
                    {renderReadOnlyValue(asset.name)}
                </div>
            ) : (
                <InputField
                    label={getText('msg_709a23220f2c')}
                    onChange={value => onChangeAsset('name', value)}
                    maxChars={256}
                    required={true}
                    placeholder={getText('msg_b7ffb9789f09')}
                    value={asset.name}
                />
            )}

            {isAssetLibraryReadOnly ? (
                <div className={styles.sectionMarginBottom24}>
                    <div className="file-form-label">
                        {isResourceLibrary
                            ? 'Alt Text'
                            : getText('msg_1910e1df6652')}
                        <Tooltip
                            html="Add an asset description to enhance search and enable screen readers to provide context to anyone who requires additional accessibility."
                            placement="right"
                        >
                            <i
                                className={`bi bi-info-circle ${styles.infoIcon}`}
                            ></i>
                        </Tooltip>
                    </div>
                    {renderReadOnlyValue(asset.description)}
                </div>
            ) : (
                <TextAreaField
                    label={getText('msg_1910e1df6652')}
                    onChange={value => onChangeAsset('description', value)}
                    maxChars={200}
                    tooltip={getText('msg_73f1aadae1ad')}
                    placeholder={getText('msg_3ae3bf00ff41')}
                    value={asset.description}
                />
            )}

            {isResourceLibrary && (
                <>
                    <TextAreaField
                        label={getText('msg_0efc2e6be4c2')}
                        onChange={value => onChangeAsset('overview', value)}
                        maxChars={10000}
                        value={asset.overview}
                    />
                    <TextAreaField
                        label={getText('msg_5bcd602254f3')}
                        onChange={value => onChangeAsset('transcript', value)}
                        maxChars={10000}
                        value={asset.transcript}
                    />
                    {Object.entries(ResourceScope).map(([scope, value]) => (
                        <CheckboxField
                            key={scope}
                            label={scope}
                            checked={
                                (asset.resourceScopeMask & value) === value
                            }
                            onChange={checked =>
                                onChangeAsset(
                                    'resourceScopeMask',
                                    checked
                                        ? (asset.resourceScopeMask || 0) | value
                                        : (asset.resourceScopeMask || 0) &
                                              ~value,
                                )
                            }
                        />
                    ))}
                    <div className="d-flex align-items-center gap-2 mb-4">
                        <div className="form-switch form-switch-locked m-0">
                            <input
                                className="form-check-input custom-switch-input"
                                type="checkbox"
                                checked={!asset.isHidden}
                                disabled={isAssetLibraryReadOnly}
                                onChange={event =>
                                    onChangeAsset(
                                        'isHidden',
                                        !event.target.checked,
                                    )
                                }
                            />
                        </div>
                        <div className="file-form-label m-0">
                            {getText('msg_1fe59390acc1')}
                        </div>
                    </div>
                </>
            )}

            <div className={styles.tagsSection}>
                <div className="file-form-label">
                    {getText('msg_848eed0fbd54')}
                    <Tooltip
                        html={getText('msg_79449ece0a91')}
                        placement="right"
                    >
                        <i
                            className={`bi bi-info-circle ${styles.infoIcon}`}
                        ></i>
                    </Tooltip>
                </div>
                {/* tagInputRef is passed from AssetDrawer so the Save button can call
            tagInputRef.current.flush() to commit any pending typed-but-not-yet-
            confirmed input before the save operation runs. */}
                {isAssetLibraryReadOnly ? (
                    <div className={styles.readOnlyTagsContainer}>
                        {(asset.assetTags || []).length > 0
                            ? (asset.assetTags || []).map(tag => (
                                  <span key={tag} className={styles.tagPill}>
                                      {tag}
                                  </span>
                              ))
                            : renderReadOnlyValue('')}
                    </div>
                ) : (
                    <TagInput
                        ref={tagInputRef}
                        onTagsChange={tags => onChangeAsset('assetTags', tags)}
                        savedTags={asset.assetTags}
                    />
                )}
            </div>
            {!isMyAssets && !isGlobalAssets && (
                <>
                    {isAssetLibraryReadOnly ? (
                        <>
                            <div className={styles.sectionMarginBottom24}>
                                <div className="file-form-label">
                                    {getText('msg_0c359a10ae17')}
                                </div>
                                {renderReadOnlyValue(
                                    asset.assetTypeId === 1 ? 'Yes' : 'No',
                                )}
                            </div>
                            <div className={styles.sectionMarginBottom26}>
                                <div className="file-form-label">
                                    {getText('msg_a798882f1c31')}
                                </div>
                                {renderReadOnlyValue(
                                    asset.isLocked ? 'Yes' : 'No',
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <SwitchField
                                label={getText('msg_0c359a10ae17')}
                                onChange={isChecked =>
                                    onChangeAsset(
                                        'assetTypeId',
                                        isChecked ? 1 : 2,
                                    )
                                }
                                tooltip={getText('msg_099a3659d191')}
                                checked={asset.assetTypeId === 1}
                            />
                            <CheckboxField
                                label={getText('msg_a798882f1c31')}
                                onChange={isChecked =>
                                    onChangeAsset('isLocked', isChecked)
                                }
                                iconRight={
                                    <i
                                        className={`bi bi-lock-fill ${styles.lockIcon}`}
                                    />
                                }
                                checked={asset.isLocked}
                            />
                        </>
                    )}
                </>
            )}
            <hr className={styles.sectionDivider} />
            {!isGlobalAssets && (!isMyAssets || allowAssetSharing) && (
                <>
                    <div className={styles.shareSection}>
                        <div className="file-form-label">
                            {getText('msg_8d1e670cccad')}
                            <Tooltip
                                html={
                                    isMyAssets
                                        ? getText('msg_219ec8bfd4cd')
                                        : getText('msg_51b0ed11a997')
                                }
                                placement="right"
                            >
                                <i
                                    className={`bi bi-info-circle ${styles.infoIcon}`}
                                ></i>
                            </Tooltip>
                        </div>
                        {isAssetLibraryReadOnly ? (
                            <div className={styles.marginTop8}>
                                {renderReadOnlyValue(
                                    getDisplayNames(asset.assetIndividuals),
                                )}
                            </div>
                        ) : (
                            <SearchableDropdown
                                placeholderLabel={getText('msg_80edab446ee5')}
                                options={individualsDropdownArray}
                                optionIDField="id"
                                savedOptions={asset.assetIndividuals || []}
                                savedOptionIDField="id"
                                updateOptions={selectedOptions =>
                                    onChangeAsset(
                                        'assetIndividuals',
                                        selectedOptions,
                                    )
                                }
                                disabled={false}
                                showTags={true}
                            />
                        )}
                        {!isMyAssets && (
                            <div className={styles.marginTop20}>
                                {isAssetLibraryReadOnly ? (
                                    <>
                                        <div className="file-form-label">
                                            {getText('msg_e744dd6b3c48')}
                                        </div>
                                        <div className={styles.marginTop8}>
                                            {renderReadOnlyValue(
                                                getDisplayNames(
                                                    asset.assetGroups,
                                                ),
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <SearchableDropdown
                                        placeholderLabel={getText(
                                            'msg_67d89dcae069',
                                        )}
                                        options={popGroupsDropdownArray}
                                        optionIDField="id"
                                        savedOptions={asset.assetGroups || []}
                                        savedOptionIDField="id"
                                        updateOptions={selectedOptions =>
                                            onChangeAsset(
                                                'assetGroups',
                                                selectedOptions,
                                            )
                                        }
                                        disabled={false}
                                        showTags={true}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    {!isAssetLibraryReadOnly && (
                        <div className={styles.requiredFieldNote}>
                            <span className={styles.requiredFieldAsterisk}>
                                *
                            </span>
                            <span className={styles.requiredFieldText}>
                                {getText('msg_04148d61b0b7')}
                            </span>
                        </div>
                    )}
                    <hr className={styles.sectionDivider} />
                </>
            )}
            <div
                className={`${styles.container} ${styles.sectionMarginBottom26}`}
            >
                {!isMyAssets && (
                    <div className={styles.column}>
                        <p className={styles.heading}>
                            {getText('msg_e59e8f15f760')}
                        </p>
                        <div className={styles.inlineItems}>
                            <p
                                className={
                                    asset.portals?.length > 0
                                        ? `${styles.active} ${styles.clickable}`
                                        : undefined
                                }
                                onClick={
                                    asset.portals?.length > 0
                                        ? onNavigateToPortals
                                        : undefined
                                }
                            >
                                {getText('msg_f0c1deecfcc5')}
                                {asset.portals?.length ?? 0})
                            </p>
                            <p
                                className={
                                    asset.notifications?.length > 0
                                        ? `${styles.active} ${styles.clickable}`
                                        : undefined
                                }
                                onClick={
                                    asset.notifications?.length > 0
                                        ? onNavigateToNotifications
                                        : undefined
                                }
                            >
                                {getText('msg_9d0e5d311171')}
                                {asset.notifications?.length ?? 0})
                            </p>
                            <p
                                className={
                                    asset.containers?.length > 0
                                        ? `${styles.active} ${styles.clickable}`
                                        : undefined
                                }
                                onClick={
                                    asset.containers?.length > 0
                                        ? onNavigateToTemplates
                                        : undefined
                                }
                            >
                                {getText('msg_a74a8a512d0b')}
                                {asset.containers?.length ?? 0})
                            </p>
                        </div>
                    </div>
                )}
                <div className={styles.column}>
                    <div className={styles.historyHeaderRow}>
                        <p className={styles.heading}>
                            {getText('msg_90ccd6497400')}
                        </p>
                        <p
                            className={`${styles.heading} ${styles.viewAllLink}`}
                            onClick={onNavigateToHistory}
                        >
                            {getText('msg_efd83559209c')}
                        </p>
                    </div>
                    <div
                        className={
                            isMyAssets || asset.assetTypeId !== 3
                                ? styles.historyRowLeft
                                : styles.historyRowSpaceBetween
                        }
                    >
                        <div className={styles.historyItem}>
                            <p>{getText('msg_accf40c89baa')}</p>
                            <p>{formatDateToMMDDYYYY(asset.createdAt)}</p>
                        </div>
                        {!isMyAssets && asset.assetTypeId === 3 && (
                            <div className={styles.historyItem}>
                                <p>{getText('msg_43de2bcd6337')}</p>
                                <p>{asset.createdBy}</p>
                            </div>
                        )}
                        <div className={styles.historyItem}>
                            <p>{getText('msg_0c79adcf41ec')}</p>
                            <p>{formatDateToMMDDYYYY(asset.updatedAt)}</p>
                        </div>
                        {!isMyAssets && asset.assetTypeId === 3 && (
                            <div className={styles.historyItem}>
                                <p>{getText('msg_6180d5db3ed9')}</p>
                                <p>{asset.updatedBy || '-'}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AssetDetailsPanel;
